import {
  createJob,
  listJobs,
  parseJob,
  uploadCadFiles,
} from "../jobApi";
import { ENGINE_BASE, engineFetch } from "../config";
import type { CadPick, Platform, RecentEntry, WorkSubmission } from "./types";

const RECENTS_KEY = "sc_recents_v1";

function loadLocalRecents(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RecentEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveRecents(entries: RecentEntry[]) {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(entries.slice(0, 30)));
  } catch {
    /* ignore quota */
  }
}

/** Prefer durable org history from the engine; fall back to this-browser cache. */
async function loadRecentsFromServerOrLocal(): Promise<RecentEntry[]> {
  try {
    const jobs = await listJobs(40);
    if (Array.isArray(jobs) && jobs.length > 0) {
      return jobs
        .filter((j) => typeof j?.job_id === "string" && j.job_id)
        .map((j) => {
          const label = j.file_name || j.drawing_id || j.job_id;
          return {
            // Selection key is job_id so reopen can call GET /v1/jobs/{id}.
            drawing_id: j.job_id,
            job_id: j.job_id,
            file_path: label || undefined,
            name: label || undefined,
            status: j.status,
            items: typeof j.items === "number" ? j.items : undefined,
            created_at: j.created_at ?? null,
            updated_at: j.updated_at ?? null,
          } satisfies RecentEntry;
        });
    }
  } catch {
    /* offline / unauthenticated — use local cache */
  }
  return loadLocalRecents();
}

function pickViaInput(multiple: boolean): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = multiple;
    input.accept = ".dxf,.dwg,.step,.stp,application/octet-stream";
    input.style.display = "none";
    document.body.appendChild(input);
    input.addEventListener("change", () => {
      const files = input.files ? Array.from(input.files) : [];
      document.body.removeChild(input);
      resolve(files);
    });
    input.addEventListener("cancel", () => {
      document.body.removeChild(input);
      resolve([]);
    });
    input.click();
  });
}

export function createBrowserPlatform(): Platform {
  return {

    async pickCadFiles(opts) {
      const files = await pickViaInput(opts?.multiple !== false);
      return files
        .filter((f) => /\.(dxf|dwg|step|stp)$/i.test(f.name))
        .map((file) => ({ name: file.name, file }));
    },

    async submitCadWork(files, master, prefs, _locale, opts) {
      const fileList = files
        .map((f) => f.file)
        .filter((f): f is File => !!f);
      if (fileList.length === 0 && master.file) fileList.push(master.file);
      if (fileList.length === 0) {
        throw new Error("Tarayıcıda dosya seçilmedi (File nesnesi gerekli)");
      }

      const uploads = await uploadCadFiles(fileList, opts?.onUploadProgress);
      if (uploads.length === 0) throw new Error("Upload boş döndü");

      // Match master by name
      const masterName = master.name;
      let masterUp = uploads.find((u) => u.original_name === masterName);
      if (!masterUp) masterUp = uploads[0];

      const uploadIds = uploads.map((u) => u.upload_id);
      const job = await createJob({
        files: [],
        upload_ids: uploadIds,
        master_upload_id: masterUp.upload_id,
      });

      // Web: always server auto (no on-prem/legacy mode picker, no cloud burst toggle).
      void prefs;
      const parsed = await parseJob(job.job_id, {
        parse_mode: null,
        cloud_burst: false,
        include_production_cost: true,
      });

      return {
        jobId: parsed.job_id,
        drawingId: parsed.drawing_id || parsed.job_id,
        job: parsed,
        status: parsed.status,
      } satisfies WorkSubmission;
    },

    async downloadExport(urlOrPath, filename) {
      let url = urlOrPath;
      if (url.startsWith("/")) {
        url = `${ENGINE_BASE}${url}`;
      } else if (!url.startsWith("http")) {
        url = `${ENGINE_BASE}/${url}`;
      }
      const res = await engineFetch(url);
      if (!res.ok) throw new Error(`İndirme başarısız (${res.status})`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    },

    recents: {
      async list() {
        return loadRecentsFromServerOrLocal();
      },
      async push(entry) {
        const key = entry.job_id || entry.drawing_id;
        const prev = loadLocalRecents().filter(
          (r) => (r.job_id || r.drawing_id) !== key && r.drawing_id !== entry.drawing_id,
        );
        saveRecents([
          {
            ...entry,
            name: entry.name || entry.file_path || entry.drawing_id,
            job_id: entry.job_id,
          },
          ...prev,
        ]);
      },
    },
  };
}

/** Build CadPick list from a DataTransfer (HTML5 drop). */
export function picksFromDataTransfer(dt: DataTransfer | null): CadPick[] {
  if (!dt?.files?.length) return [];
  return Array.from(dt.files)
    .filter((f) => /\.(dxf|dwg|step|stp)$/i.test(f.name))
    .map((file) => ({ name: file.name, file }));
}

export function defaultMasterPick(picks: CadPick[]): CadPick {
  const step = picks.find((p) => /\.(step|stp)$/i.test(p.name));
  return step || picks[0];
}
